import React from 'react'
import { useSelector } from '../../../../store'
import { AnalysisWorkerMessage } from './analysis-worker-protocol'
import { analyzeViewerConfig, AppAnalysis } from './code-widget-analysis'

/**
 * The Code Widget scan of the app being configured, run in a Web Worker so a slow scan costs a "did not
 * finish" instead of a frozen tab. While a new scan runs, the previous result stays on screen.
 */
export type CodeWidgetCheck =
  /** Also the state before the config has loaded: no colour until there is something to read. */
  | { state: 'checking'; analysis: AppAnalysis | null }
  | { state: 'ready'; analysis: AppAnalysis }
  /** The scan ran past CHECK_TIME_LIMIT_MS and was stopped while reading `widget`, a page worth a look in itself. */
  | { state: 'timed-out'; analysis: null; widget: string | null }
  /** The worker itself failed — a build, policy or runtime error, not anything a page did. */
  | { state: 'failed'; analysis: null; error: string }

/** Edits come in bursts; scan once they settle. */
const DEBOUNCE_MS = 300
export const CHECK_TIME_LIMIT_MS = 3000

/**
 * One scan for the whole top bar: the Lock&Publish dot, its card and Inspector Gadget all read this.
 * `enabled` is false for viewers and signed-out users, who see none of it and start no worker.
 */
export function useCodeWidgetCheck(enabled: boolean): CodeWidgetCheck {
  const config = useSelector((state) => state.currentViewer.config)
  const [check, setCheck] = React.useState<CodeWidgetCheck>({ state: 'checking', analysis: null })

  React.useEffect(
    function scanInWorker() {
      if (!enabled || config == null) return
      setCheck((previous) => ({ state: 'checking', analysis: previous.analysis }))

      let stopped = false
      let worker: Worker | null = null
      let limit: number | undefined
      let reading: string | null = null

      const settle = (next: CodeWidgetCheck) => {
        if (stopped) return
        stopped = true
        window.clearTimeout(limit)
        worker?.terminate()
        setCheck(next)
      }
      const fail = (error: unknown) => {
        const message = error instanceof Error ? error.message : String(error || 'unknown error')
        console.error('Code Widget check: the worker failed.', error)
        settle({ state: 'failed', analysis: null, error: message })
      }

      const onMessage = (event: MessageEvent<AnalysisWorkerMessage>) => {
        const message = event.data
        if (message.type === 'widget') reading = message.label
        else if (message.type === 'done') settle({ state: 'ready', analysis: message.analysis })
        else {
          /* The clock starts once the script has loaded: a cold cache or a slow link is not a slow page. */
          limit = window.setTimeout(
            () => settle({ state: 'timed-out', analysis: null, widget: reading }),
            CHECK_TIME_LIMIT_MS
          )
          try {
            worker?.postMessage(config)
          } catch (error) {
            fail(error)
          }
        }
      }

      const start = window.setTimeout(() => {
        /* Without workers (tests, very old browsers) the scan runs here: same result, no time limit. */
        if (typeof Worker === 'undefined') {
          try {
            settle({ state: 'ready', analysis: analyzeViewerConfig(config) })
          } catch (error) {
            fail(error)
          }
          return
        }
        startWorker().then((running) => {
          if (stopped) return running.terminate()
          worker = running
          running.onmessage = onMessage
          /* A worker that throws never answers; say so now, rather than wait out the limit and blame a page. */
          running.onerror = (event: ErrorEvent) => fail(event.message)
        }, fail)
      }, DEBOUNCE_MS)

      /* A newer config replaces this scan: stop waiting for it and stop the worker running it. */
      return () => {
        stopped = true
        window.clearTimeout(start)
        window.clearTimeout(limit)
        worker?.terminate()
      }
    },
    [config, enabled]
  )

  return check
}

/**
 * Loaded when a worker is first needed, not at import: the `worker-loader!` request is webpack syntax, and a
 * static import put it in front of every test and tool that loads the app without webpack (Jest among them).
 * webpack 4 has no `new Worker(new URL(…, import.meta.url))`, so worker-loader bundles the worker instead.
 * A worker the page may not start (a CSP worker-src block) throws here, and rejects the promise.
 */
function startWorker(): Promise<Worker> {
  // eslint-disable-next-line import/no-webpack-loader-syntax
  return import('worker-loader!./code-widget-analysis.worker').then(({ default: AnalysisWorker }) => new AnalysisWorker())
}
